import { useState, useMemo } from 'react';
import { Plus, RefreshCw, FileSpreadsheet, FileText, BarChart3, Users, Wallet, Palmtree, TrendingUp, Banknote, KeyRound } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useEmployees } from '@/hooks/useHr';
import { EmployeeFormDialog } from '@/components/hr/EmployeeFormDialog';
import { CreateUserDialog } from '@/components/hr/CreateUserDialog';
import { can } from '@/utils/permissions';
import { EmployeeDetailPanel } from '@/components/hr/EmployeeDetailPanel';
import {
  CONTRACT_TYPE_LABELS,
  EMPLOYEE_STATUS_LABELS,
  type ContractType,
  type Employee,
  type EmployeeStatus,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { useAuthStore } from '@/stores/authStore';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';

/**
 * Personnel RH pixel-perfect CTO-FRONT-002.
 * Référence : design-rh-personnel-v1.html
 * Layout : KPI strip 5 cards + grid 2 cols (liste à gauche + detail panel sticky droite)
 */

const initials = (name: string) =>
  name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

const yearsSince = (iso: string): number =>
  (Date.now() - new Date(iso).getTime()) / (365.25 * 24 * 3600 * 1000);

const STATUS_BADGE: Record<EmployeeStatus, string> = {
  active:     'ds-bd ds-bd-s',
  suspended:  'ds-bd ds-bd-w',
  terminated: 'ds-bd ds-bd-n',
};

// Avatar background palette tournante par initiale (pour distinguer les employés)
const AV_GRADIENTS = [
  'linear-gradient(135deg, var(--p), var(--p-light))',
  'linear-gradient(135deg, var(--info), #93c5fd)',
  'linear-gradient(135deg, var(--warning), #fcd34d)',
  'linear-gradient(135deg, #8b5cf6, #c4b5fd)',
  'linear-gradient(135deg, var(--success), #6ee7b7)',
  'linear-gradient(135deg, var(--accent), #ff8c6a)',
];
const avGradient = (s: string) => AV_GRADIENTS[s.charCodeAt(0) % AV_GRADIENTS.length];

export const PersonnelPage = () => {
  const filiale  = useAuthStore((s) => s.filiale);
  const userRole = useAuthStore((s) => s.user?.role);
  const canSeeSalary = userRole === 'daf' || userRole === 'admin_holding';

  const [search, setSearch]             = useState('');
  const [department, setDepartment]     = useState('');
  const [contractType, setContractType] = useState<ContractType | ''>('');
  const [status, setStatus]             = useState<EmployeeStatus | ''>('active');
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editing, setEditing]           = useState<Employee | undefined>();
  const [selectedId, setSelectedId]     = useState<string | null>(null);

  const { data, isLoading, refetch } = useEmployees({
    search:       search       || undefined,
    department:   department   || undefined,
    contractType: contractType || undefined,
    status:       status       || undefined,
    limit: 200,
  });
  const employees = data?.data ?? [];
  const selected  = employees.find((e) => e.id === selectedId) ?? null;

  // KPIs masse salariale + ancienneté + congés
  const kpis = useMemo(() => {
    const active = employees.filter((e) => e.status === 'active');
    let masse = 0;
    let count = 0;
    for (const e of active) {
      if (e.salaryCents) { masse += Number(BigInt(e.salaryCents)); count++; }
    }
    const avgYears = active.length > 0
      ? active.reduce((s, e) => s + yearsSince(e.hireDate), 0) / active.length
      : 0;
    const cdiCount = active.filter((e) => e.contractType === 'CDI').length;
    const cdiPct   = active.length > 0 ? Math.round((cdiCount / active.length) * 100) : 0;
    return {
      effectif: active.length,
      cdiPct,
      // Ticket SIMGROUP-2026-002 M-04 : XOF n'a pas de subdivision, on arrondit
      // au franc entier. Le "masse / 100" utilisait la convention "cents" mais
      // FCFA sans centimes → toujours arrondir au franc le plus proche.
      masseSalarialeFcfa: Math.round(masse / 100),
      anciennete: avgYears,
      salaireMoyenFcfa: count > 0 ? Math.round((masse / count) / 100) : 0,
    };
  }, [employees]);

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (e: Employee) => { setEditing(e); setDialogOpen(true); };

  // ── Export Excel / PDF ─────────────────────────────────────
  const exportColumns: ExportColumn<Employee>[] = [
    { header: 'Matricule',     accessor: (e) => e.matricule },
    { header: 'Nom complet',   accessor: (e) => e.fullName },
    { header: 'Email',         accessor: (e) => e.email ?? '—' },
    { header: 'Téléphone',     accessor: (e) => e.phone ?? '—' },
    { header: 'Département',   accessor: (e) => e.department },
    { header: 'Poste',         accessor: (e) => e.jobTitle },
    { header: 'Contrat',       accessor: (e) => CONTRACT_TYPE_LABELS[e.contractType] },
    { header: 'Embauche',      accessor: (e) => new Date(e.hireDate).toLocaleDateString('fr-FR') },
    { header: 'Statut',        accessor: (e) => EMPLOYEE_STATUS_LABELS[e.status] },
    ...(canSeeSalary ? [{
      header: 'Salaire (FCFA)',
      accessor: (e: Employee) => e.salaryCents ? Number(BigInt(e.salaryCents)) / 100 : 0,
      align: 'right' as const,
    }] : []),
  ];

  const onExportXLSX = () => exportToXLSX({
    filename:  buildExportFilename('Personnel', filiale?.filialeKey, 'xlsx'),
    sheetName: 'Personnel RH',
    columns:   exportColumns,
    rows:      employees,
  });

  const onExportPDF = () => exportToPDF({
    title:    `Personnel — ${filiale?.fullName ?? ''}`,
    subtitle: `${employees.length} employé${employees.length > 1 ? 's' : ''}${canSeeSalary ? ` · Masse salariale ${formatNumber(kpis.masseSalarialeFcfa)} FCFA/mois` : ''}`,
    filename: buildExportFilename('Personnel', filiale?.filialeKey, 'pdf'),
    orientation: 'landscape',
    columns:  exportColumns,
    rows:     employees,
  });

  return (
    <>
      <Topbar
        title="Personnel RH"
        subtitle={filiale ? `Effectif & masse salariale · ${filiale.fullName}` : 'Personnel RH'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportXLSX()} disabled={employees.length === 0} title="Exporter Excel">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportPDF()} disabled={employees.length === 0} title="Exporter PDF">
              <FileText size={14} /> PDF
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" disabled title="Bientôt"><BarChart3 size={14} /> Rapport RH</button>
            {can(userRole, 'users.create') && (
              <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setUserDialogOpen(true)} title="Créer un compte de connexion à la plateforme">
                <KeyRound size={14} /> Créer compte
              </button>
            )}
            {can(userRole, 'rh.employees.manage') && (
              <button className="ds-btn ds-btn-p" onClick={openCreate}>
                <Plus size={14} /> Ajouter employé
              </button>
            )}
          </>
        }
      />

      <div className="ds-content">
        {/* KPI Strip 5 cards */}
        <div className="ds-kpi-strip cols-5">
          <div className="ds-ks ks-teal">
            <div className="ds-ks-ico" style={{ background: 'var(--p-soft)' }}><Users size={16} /></div>
            <div className="ds-ks-label">Effectif actif</div>
            <div className="ds-ks-value">{kpis.effectif}</div>
            <div className="ds-ks-sub">{kpis.cdiPct}% CDI</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-ico" style={{ background: '#dbeafe' }}><Wallet size={16} /></div>
            <div className="ds-ks-label">Masse salariale</div>
            <div className="ds-ks-value" style={{ fontSize: 14 }}>
              {canSeeSalary ? formatNumber(kpis.masseSalarialeFcfa) : '● ● ●'}
            </div>
            <div className="ds-ks-sub">{canSeeSalary ? 'FCFA / mois' : 'DAF only'}</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-ico" style={{ background: '#fef3c7' }}><Palmtree size={16} /></div>
            <div className="ds-ks-label">Congés en cours</div>
            <div className="ds-ks-value">0</div>
            <div className="ds-ks-sub">Module à venir</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-ico" style={{ background: '#f3e8ff' }}><TrendingUp size={16} /></div>
            <div className="ds-ks-label">Ancienneté moy.</div>
            <div className="ds-ks-value">{kpis.anciennete.toFixed(1)} <span style={{ fontSize: 12 }}>ans</span></div>
            <div className="ds-ks-sub">Équipe stable</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-ico" style={{ background: '#d1fae5' }}><Banknote size={16} /></div>
            <div className="ds-ks-label">Salaire moyen</div>
            <div className="ds-ks-value" style={{ fontSize: 14 }}>
              {canSeeSalary ? formatNumber(kpis.salaireMoyenFcfa) : '● ● ●'}
            </div>
            <div className="ds-ks-sub">{canSeeSalary ? 'FCFA / mois' : 'DAF only'}</div>
          </div>
        </div>

        {/* Layout liste + détail */}
        <div className="ds-layout-2">

          {/* LEFT : Filters + emp list */}
          <div>
            <div className="ds-filters">
              <div className="ds-sw">
                <input
                  className="ds-fi ds-fi-search"
                  type="search"
                  placeholder="Rechercher par nom, poste, matricule..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <input
                className="ds-fi"
                style={{ width: 'auto' }}
                placeholder="Département"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
              <select
                className="ds-fi"
                style={{ width: 'auto' }}
                value={contractType}
                onChange={(e) => setContractType(e.target.value as ContractType | '')}
              >
                <option value="">Tous contrats</option>
                {(Object.keys(CONTRACT_TYPE_LABELS) as ContractType[]).map((t) => (
                  <option key={t} value={t}>{CONTRACT_TYPE_LABELS[t]}</option>
                ))}
              </select>
              <select
                className="ds-fi"
                style={{ width: 'auto' }}
                value={status}
                onChange={(e) => setStatus(e.target.value as EmployeeStatus | '')}
              >
                <option value="">Tous statuts</option>
                {(Object.keys(EMPLOYEE_STATUS_LABELS) as EmployeeStatus[]).map((s) => (
                  <option key={s} value={s}>{EMPLOYEE_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>

            {isLoading ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
            ) : employees.length === 0 ? (
              <div className="ds-panel" style={{ padding: 48, textAlign: 'center' }}>
                <Users size={36} style={{ margin: '0 auto 12px', color: 'var(--tx-3)' }} />
                <p style={{ fontSize: 14, color: 'var(--tx-2)' }}>Aucun employé</p>
                <button className="ds-btn ds-btn-p" style={{ marginTop: 12 }} onClick={openCreate}>
                  <Plus size={14} /> Ajouter le premier employé
                </button>
              </div>
            ) : (
              <div className="ds-emp-list">
                {employees.map((e) => {
                  const isSel = e.id === selectedId;
                  const salary = e.salaryCents ? Number(BigInt(e.salaryCents)) / 100 : null;
                  return (
                    <div
                      key={e.id}
                      className={`ds-emp-card${isSel ? ' selected' : ''}`}
                      onClick={() => setSelectedId(e.id)}
                    >
                      <div className="ds-emp-av" style={{ background: avGradient(e.fullName) }}>
                        {initials(e.fullName)}
                        <span className={`ds-emp-av-status ${e.status === 'active' ? 'on' : 'off'}`} />
                      </div>
                      <div className="ds-emp-info">
                        <div className="ds-emp-name">{e.fullName}</div>
                        <div className="ds-emp-role">{e.jobTitle}</div>
                        <div className="ds-emp-dept">{e.department} · {e.matricule}</div>
                      </div>
                      <div className="ds-emp-right">
                        <span className={STATUS_BADGE[e.status]}>{EMPLOYEE_STATUS_LABELS[e.status]}</span>
                        {canSeeSalary && salary != null && (
                          <div className="ds-emp-salary">{formatNumber(salary)} FCFA</div>
                        )}
                        <div className="ds-emp-since">{CONTRACT_TYPE_LABELS[e.contractType]}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT : Detail panel sticky */}
          <EmployeeDetailPanel employee={selected} onEdit={openEdit} />
        </div>
      </div>

      <EmployeeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} employee={editing} />
      <CreateUserDialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} />
    </>
  );
};
